// Hydrate side of hybrid persistence (APP-2705): seed the dashboard slices for an instance
// from its saved WorkspaceStateV1 blob. The counterpart of serializeWorkspace.
//
// A saved workspace WINS over the layout seed: runs after initFromLayoutConfig (the baseline) and
// overwrites with the instance's own state, restoring ai-artifact and the dashboard-level core/model
// state the layout config doesn't carry.
//
// No client-side validation — server is the validator of record, schema is permissive.
// Each slice's setPreferences whitelists its own fields, so deploy-skew unknown keys drop at the reducer.
import type { AppDispatch } from "../../../../store"
import { getPanelIdForModule } from "../helpers/module-renderers"
import { coreSlice } from "../store/core-slice"
import { modelSlice } from "../store/model-slice"
import { tradeSlice } from "../store/trade-slice"
import { optimizationSlice } from "../store/optimization-slice"
import { panelsSlice } from "../store/panels-slice"
import { assetViewSlice } from "../store/asset-view-slice"
import { factorsViewSlice } from "../store/factors-view-slice"
import { graphSlice } from "../store/graph-slice"
import { kpiSlice } from "../store/kpi-slice"
import type { DashboardModule, ModuleType } from "../modules/types"
import type { ModuleEntry, WorkspaceStateV1 } from "../contracts/layouts"

// Per-panel setPreferences accepts the portable viewConfig fields directly (same shape the layout
// bridge seeds), so each viewConfig fans into its owning slice without re-deriving it.
type PanelSeed = { dashboardId: string, panelId: string } & Record<string, unknown>

function toModule(entry: ModuleEntry): DashboardModule {
  // entry.type is a server-validated module type (the client trusts server responses);
  // the panels slice's validateModules drops any type that isn't registered, so this narrowing
  // cannot smuggle an unrenderable module past the reducer.
  return { id: entry.id, type: entry.type as ModuleType, config: entry.config ?? {} }
}

function seedPanel(
  dispatch: AppDispatch,
  dashboardId: string,
  module: DashboardModule,
  viewConfig: Record<string, unknown> | undefined,
): void {
  if (!viewConfig) return
  // Per-panel slots are keyed by the derived panel id (getPanelIdForModule), not the raw module
  // id — match initFromLayoutConfig so the layout seed and the workspace hydrate hit the same slot.
  const payload: PanelSeed = { dashboardId, panelId: getPanelIdForModule(module), ...viewConfig }
  switch (module.type) {
    case "asset-view":
      dispatch(assetViewSlice.actions.setPreferences(payload))
      return
    case "factors":
      dispatch(factorsViewSlice.actions.setPreferences(payload))
      return
    case "graph":
      dispatch(graphSlice.actions.setPreferences(payload))
      return
    case "kpi":
      dispatch(kpiSlice.actions.setPreferences(payload))
      return
    default:
      // ai-artifact and unknown types carry no per-panel slice state.
  }
}

/**
 * Seed all dashboard slices for `dashboardId` from a saved WorkspaceStateV1.
 *
 * Order mirrors initFromLayoutConfig: panels list first (so the slots exist), then each panel's
 * viewConfig, then the dashboard-level core/model/trade/optimization state. Idempotent — each
 * setPreferences marks the slot preferences-loaded, so the sync hooks treat it as authoritative
 * and do not re-load from the per-subtype preference rows.
 */
export function hydrateWorkspace(
  dispatch: AppDispatch,
  dashboardId: string,
  workspace: WorkspaceStateV1,
): void {
  const modules = (workspace.modules ?? []).map(toModule)
  // Restore the full module list (including ai-artifact) so pinned artifacts reopen.
  dispatch(panelsSlice.actions.setPreferences({ dashboardId, modules }))

  // Panels are keyed by module.id, matching panel.key in serialize-workspace.ts — keep the two in sync.
  const panelConfigs = workspace.panels ?? {}
  for (const module of modules) {
    seedPanel(dispatch, dashboardId, module, panelConfigs[module.id])
  }

  const core = workspace.core
  if (core) {
    const { modelId, ...coreFields } = core
    // Core slice whitelists CORE_PREFERENCE_FIELDS; modelId belongs to the model slice.
    dispatch(coreSlice.actions.setPreferences({ dashboardId, ...coreFields }))
    dispatch(modelSlice.actions.setPreferences({ dashboardId, modelId: modelId ?? null }))
  }

  // trade/optimization travel in the permissive blob's open-ended fields when present.
  const trade = workspace.trade
  if (trade && typeof trade === "object" && typeof (trade as { isExpanded?: unknown }).isExpanded === "boolean") {
    dispatch(tradeSlice.actions.setPreferences({ dashboardId, isExpanded: (trade as { isExpanded: boolean }).isExpanded }))
  }
  const optimization = workspace.optimization
  if (optimization && typeof optimization === "object") {
    dispatch(optimizationSlice.actions.setPreferences({ dashboardId, ...(optimization as Record<string, unknown>) }))
  }
}
